import { useState, useEffect, useMemo } from 'react';
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { useTenant } from './useTenant';

interface UseSupabaseAssetOptions {
  enabled?: boolean;
  fallbackUrl?: string;
}

interface UseSupabaseAssetResult {
  url: string | null;
  isLoading: boolean;
  error: string | null;
  exists: boolean;
}

// URL cache to avoid reconstructing on every render
const urlCache = new Map<string, string>();

// Asset existence cache to avoid repeated HEAD requests
const existenceCache = new Map<string, boolean>();

/**
 * Custom hook to construct and validate Supabase storage asset URLs
 * 
 * @param relativePath - Relative path within the storage bucket (e.g., 'logos/gmct-logo.png')
 * @param options - Configuration options
 * @returns Object with URL, loading state, error, and existence status
 */
export function useSupabaseAsset(
  relativePath: string | null | undefined,
  options: UseSupabaseAssetOptions = {}
): UseSupabaseAssetResult {
  const { enabled = true, fallbackUrl } = options;
  const { tenant } = useTenant();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(true); // Assume exists until proven otherwise

  // Construct the cache key
  const cacheKey = useMemo(() => {
    if (!relativePath || !tenant?.id) return null;
    const bucketName = tenant.storage_bucket_name || 'assets';
    return `${tenant.id}:${bucketName}:${relativePath}`;
  }, [relativePath, tenant?.id, tenant?.storage_bucket_name]);

  // Construct the full URL
  const url = useMemo(() => {
    if (!enabled || !relativePath || !tenant?.id) {
      return fallbackUrl || null;
    }

    // Check cache first
    if (cacheKey && urlCache.has(cacheKey)) {
      return urlCache.get(cacheKey)!;
    }

    // Get Supabase URL from environment
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      console.warn('⚠️ VITE_SUPABASE_URL not configured for asset loading');
      return fallbackUrl || null;
    }

    // Get bucket name from tenant config or use default
    const bucketName = tenant.storage_bucket_name || 'assets';
    
    // Construct full URL
    const fullUrl = `${supabaseUrl}/storage/v1/object/public/${bucketName}/${relativePath}`;
    
    // Cache the constructed URL
    if (cacheKey) {
      urlCache.set(cacheKey, fullUrl);
    }
    
    return fullUrl;
  }, [enabled, relativePath, tenant?.id, tenant?.storage_bucket_name, fallbackUrl, cacheKey]);

  // Check if asset exists (only if Supabase is available and we have a URL)
  useEffect(() => {
    if (!enabled || !url || !cacheKey || !isSupabaseAvailable()) {
      return;
    }

    // Check existence cache first
    if (existenceCache.has(cacheKey)) {
      setExists(existenceCache.get(cacheKey)!);
      return;
    }

    const checkAssetExists = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use HEAD request to check if asset exists without downloading it
        const response = await fetch(url, { method: 'HEAD' });
        const assetExists = response.ok;
        
        // Cache the result
        existenceCache.set(cacheKey, assetExists);
        setExists(assetExists);

        if (!assetExists) {
          console.warn(`⚠️ Asset not found: ${url}`);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to check asset existence';
        console.error('❌ Error checking asset existence:', errorMessage);
        setError(errorMessage);
        setExists(false);
        
        // Cache negative result
        existenceCache.set(cacheKey, false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAssetExists();
  }, [enabled, url, cacheKey]);

  return {
    url,
    isLoading,
    error,
    exists
  };
}

/**
 * Utility function to clear the asset cache (useful for testing or cache invalidation)
 */
export function clearSupabaseAssetCache(): void {
  urlCache.clear();
  existenceCache.clear();
}

/**
 * Utility function to preload an asset (useful for critical assets)
 */
export function preloadSupabaseAsset(relativePath: string, tenantId: string, bucketName = 'assets'): void {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) return;

  const fullUrl = `${supabaseUrl}/storage/v1/object/public/${bucketName}/${relativePath}`;
  const cacheKey = `${tenantId}:${bucketName}:${relativePath}`;
  
  // Cache the URL
  urlCache.set(cacheKey, fullUrl);
  
  // Preload the image
  const img = new Image();
  img.src = fullUrl;
  img.onload = () => {
    existenceCache.set(cacheKey, true);
  };
  img.onerror = () => {
    existenceCache.set(cacheKey, false);
  };
}